// Shared inline styles for the student-system pages (Classes, Class detail, My Classes),
// on the same tokens and page header as Users / Join Requests so the admin pages read as
// one app.
import React from 'react';
import { T } from '../gamification/shared';
import type { ClassRole } from '../../services/classService';

export const pageStyle: React.CSSProperties = { padding: '2rem', fontFamily: T.font, width: '100%' };

export const headerRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '1rem',
  flexWrap: 'wrap',
  marginBottom: '1.5rem',
};

export const titleStyle: React.CSSProperties = { fontSize: '1.75rem', fontWeight: 600, margin: '0 0 0.25rem', color: '#000' };

export const subtitleStyle: React.CSSProperties = { color: '#6b7280', margin: 0, fontSize: '0.875rem' };

export const cardStyle: React.CSSProperties = {
  background: T.card,
  border: `1px solid ${T.border}`,
  borderRadius: T.radius,
  boxShadow: T.shadow,
  padding: '1.25rem',
  marginBottom: '1.25rem',
};

export const sectionHeadingStyle: React.CSSProperties = { fontSize: '1rem', fontWeight: 700, color: T.text, margin: '0 0 0.75rem' };

export const hintStyle: React.CSSProperties = { fontSize: '0.8rem', color: T.muted, lineHeight: 1.6, margin: '6px 0 0' };

export const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.78rem',
  fontWeight: 700,
  color: T.muted,
  marginBottom: 6,
};

export const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: `1px solid ${T.border}`,
  borderRadius: 8,
  fontSize: '0.85rem',
  fontFamily: T.font,
  color: T.text,
  boxSizing: 'border-box',
};

const buttonBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem',
  padding: '0.5rem 1rem',
  borderRadius: '0.375rem',
  fontFamily: T.font,
  fontWeight: 500,
  fontSize: '0.85rem',
  cursor: 'pointer',
};

export const primaryButtonStyle: React.CSSProperties = {
  ...buttonBase,
  background: T.primary,
  color: '#fff',
  border: `1.5px solid ${T.primary}`,
};

export const secondaryButtonStyle: React.CSSProperties = {
  ...buttonBase,
  background: '#fff',
  color: T.primary,
  border: `1.5px solid ${T.primaryLight}`,
};

export const linkButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: T.primary,
  fontFamily: T.font,
  fontSize: '0.82rem',
  fontWeight: 600,
  cursor: 'pointer',
};

export const dangerLinkStyle: React.CSSProperties = { ...linkButtonStyle, color: '#b91c1c' };

export const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' };

export const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.6rem 0.75rem',
  borderBottom: `1px solid ${T.border}`,
  color: T.muted,
  fontWeight: 600,
  fontSize: '0.78rem',
  whiteSpace: 'nowrap',
};

export const tdStyle: React.CSSProperties = { padding: '0.65rem 0.75rem', borderBottom: `1px solid ${T.border}`, color: T.text };

export const errorStyle: React.CSSProperties = {
  background: '#fef2f2',
  border: '1px solid #fecaca',
  color: '#991b1b',
  borderRadius: 8,
  padding: '0.6rem 0.8rem',
  fontSize: '0.85rem',
  marginBottom: '1rem',
};

export const noticeStyle: React.CSSProperties = {
  ...errorStyle,
  background: '#f0fdf4',
  border: '1px solid #bbf7d0',
  color: '#166534',
};

const ROLE_COLORS: Record<ClassRole | 'shared', { bg: string; text: string }> = {
  teacher: { bg: '#dbeafe', text: '#1e40af' },
  shared: { bg: '#fae8ff', text: '#86198f' },
  co_teacher: { bg: '#e0e7ff', text: '#3730a3' },
  student: { bg: '#f1f5f9', text: '#475569' },
};

export const roleBadgeStyle = (role: ClassRole | 'shared'): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: '0.75rem',
  fontWeight: 600,
  background: ROLE_COLORS[role]?.bg ?? '#f1f5f9',
  color: ROLE_COLORS[role]?.text ?? T.muted,
});

export const statusBadgeStyle = (ok: boolean): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: '0.75rem',
  fontWeight: 600,
  background: ok ? '#dcfce7' : '#fef3c7',
  color: ok ? '#166534' : '#92400e',
});

/** The server's `detail` when it sent one, otherwise the fallback. */
export const errorMessage = (err: unknown, fallback: string): string => {
  const message = (err as { message?: string })?.message;
  return message && message.trim() ? message : fallback;
};
